import React, { useCallback, useState } from "react";
import { Button } from "../../../../design/button";
import { Input } from "../../../../design/input";
import { Textarea } from "../../../../design/textarea";
import { Select } from "../../../../design/select";
import { Checkbox, CheckboxField } from "../../../../design/checkbox";
import {
  Field,
  Fieldset,
  FieldGroup,
  Label,
  Description,
} from "../../../../design/fieldset";
import { Divider } from "../../../../design/divider";
import {
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/20/solid";
import { FaceSmileIcon } from "@heroicons/react/24/outline";
import {
  WizardFormData,
  ChildFormData,
  GENDER_OPTIONS,
  VICTIMIZATION_TYPE_OPTIONS,
  createEmptyChild,
} from "./types";

interface ChildrenStepProps {
  formData: WizardFormData;
  setFormData: React.Dispatch<React.SetStateAction<WizardFormData>>;
  onNext: () => void;
  onBack: () => void;
}

const ChildrenStep: React.FC<ChildrenStepProps> = ({
  formData,
  setFormData,
  onNext,
  onBack,
}) => {
  const [expandedChildren, setExpandedChildren] = useState<Record<string, boolean>>({});

  const toggleExpanded = (childId: string) => {
    setExpandedChildren((prev) => ({
      ...prev,
      [childId]: !prev[childId],
    }));
  };

  const handleChildChange = useCallback(
    (index: number, field: keyof ChildFormData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData((prev) => {
          const newChildren = [...prev.children];
          newChildren[index] = {
            ...newChildren[index],
            [field]: e.target.value,
          };
          return { ...prev, children: newChildren };
        });
      },
    [setFormData]
  );

  const handleCheckboxChange = useCallback(
    (index: number, field: keyof ChildFormData) => (checked: boolean) => {
      setFormData((prev) => {
        const newChildren = [...prev.children];
        newChildren[index] = {
          ...newChildren[index],
          [field]: checked,
        };
        return { ...prev, children: newChildren };
      });
    },
    [setFormData]
  );

  const handleAddChild = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      children: [...prev.children, createEmptyChild()],
    }));
  }, [setFormData]);

  const handleRemoveChild = useCallback(
    (index: number) => {
      setFormData((prev) => ({
        ...prev,
        children: prev.children.filter((_, i) => i !== index),
      }));
    },
    [setFormData]
  );

  // At least one child with first and last name is required
  const isValid = formData.children.some(
    (c) => c.firstName.trim() && c.lastName.trim()
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
            Children
          </h3>
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            Add all children involved in this case
          </p>
        </div>
        <Button type="button" outline onClick={handleAddChild}>
          <PlusIcon className="h-4 w-4" />
          Add Child
        </Button>
      </div>

      {formData.children.map((child, index) => (
        <div
          key={child.id}
          className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-4"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900">
                <FaceSmileIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <span className="font-medium text-zinc-900 dark:text-zinc-100">
                Child {index + 1}
              </span>
            </div>
            {formData.children.length > 1 && (
              <Button
                type="button"
                plain
                onClick={() => handleRemoveChild(index)}
                className="text-red-600 hover:text-red-700"
              >
                <TrashIcon className="h-4 w-4" />
                Remove
              </Button>
            )}
          </div>

          <Fieldset>
            <FieldGroup>
              {/* Name fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field>
                  <Label htmlFor={`child-${index}-firstName`}>
                    First Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id={`child-${index}-firstName`}
                    type="text"
                    placeholder="First name"
                    value={child.firstName}
                    onChange={handleChildChange(index, "firstName")}
                  />
                </Field>

                <Field>
                  <Label htmlFor={`child-${index}-lastName`}>
                    Last Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id={`child-${index}-lastName`}
                    type="text"
                    placeholder="Last name"
                    value={child.lastName}
                    onChange={handleChildChange(index, "lastName")}
                  />
                </Field>
              </div>

              {/* DOB and Gender */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field>
                  <Label htmlFor={`child-${index}-dob`}>Date of Birth</Label>
                  <Input
                    id={`child-${index}-dob`}
                    type="date"
                    value={child.dateOfBirth}
                    onChange={handleChildChange(index, "dateOfBirth")}
                  />
                </Field>

                <Field>
                  <Label htmlFor={`child-${index}-gender`}>Gender</Label>
                  <Select
                    id={`child-${index}-gender`}
                    value={child.gender}
                    onChange={handleChildChange(index, "gender")}
                  >
                    <option value="">Select gender...</option>
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <Divider soft />

              {/* Special Needs */}
              <CheckboxField>
                <Checkbox
                  checked={child.hasSpecialNeeds}
                  onChange={handleCheckboxChange(index, "hasSpecialNeeds")}
                />
                <Label>Child has special needs</Label>
              </CheckboxField>

              {child.hasSpecialNeeds && (
                <Field>
                  <Label htmlFor={`child-${index}-specialNeeds`}>
                    Special Needs Details
                  </Label>
                  <Textarea
                    id={`child-${index}-specialNeeds`}
                    placeholder="Describe special needs, accommodations required, etc."
                    value={child.specialNeeds}
                    onChange={handleChildChange(index, "specialNeeds")}
                    rows={2}
                  />
                </Field>
              )}

              {/* Medical Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field>
                  <Label htmlFor={`child-${index}-allergies`}>Allergies</Label>
                  <Description>
                    List any known allergies
                  </Description>
                  <Input
                    id={`child-${index}-allergies`}
                    type="text"
                    placeholder="e.g. Peanuts, Penicillin"
                    value={child.allergies}
                    onChange={handleChildChange(index, "allergies")}
                  />
                </Field>

                <Field>
                  <Label htmlFor={`child-${index}-medicalConditions`}>Medical Conditions</Label>
                  <Description>
                    Any medical conditions staff should be aware of
                  </Description>
                  <Input
                    id={`child-${index}-medicalConditions`}
                    type="text"
                    placeholder="e.g. Asthma, Diabetes"
                    value={child.medicalConditions}
                    onChange={handleChildChange(index, "medicalConditions")}
                  />
                </Field>
              </div>

              {/* Notes */}
              <Field>
                <Label htmlFor={`child-${index}-notes`}>Additional Notes</Label>
                <Textarea
                  id={`child-${index}-notes`}
                  placeholder="Any other important information about this child..."
                  value={child.notes}
                  onChange={handleChildChange(index, "notes")}
                  rows={2}
                />
              </Field>

              {/* Collapsible Additional Information */}
              <div className="border-t border-zinc-200 dark:border-zinc-700 pt-4 mt-4">
                <button
                  type="button"
                  onClick={() => toggleExpanded(child.id)}
                  className="flex items-center justify-between w-full text-left text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-zinc-100"
                >
                  <span>Additional Information (Custody, DSS, Victimization)</span>
                  {expandedChildren[child.id] ? (
                    <ChevronUpIcon className="h-5 w-5" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5" />
                  )}
                </button>

                {expandedChildren[child.id] && (
                  <div className="mt-4 space-y-4">
                    {/* Custody/Guardian Information */}
                    <div>
                      <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                        Custody & Guardian Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Field>
                          <Label htmlFor={`child-${index}-residesWithName`}>Child Resides With</Label>
                          <Input
                            id={`child-${index}-residesWithName`}
                            type="text"
                            placeholder="Name of person child resides with"
                            value={child.residesWithName}
                            onChange={handleChildChange(index, "residesWithName")}
                          />
                        </Field>

                        <Field>
                          <Label htmlFor={`child-${index}-legalGuardianName`}>Legal Guardian</Label>
                          <Input
                            id={`child-${index}-legalGuardianName`}
                            type="text"
                            placeholder="Legal guardian's name"
                            value={child.legalGuardianName}
                            onChange={handleChildChange(index, "legalGuardianName")}
                          />
                        </Field>
                      </div>
                    </div>

                    <Divider soft />

                    {/* DSS/Social Services Information */}
                    <div>
                      <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                        DSS / Social Services
                      </h4>

                      <CheckboxField>
                        <Checkbox
                          checked={child.isInDSSCustody}
                          onChange={handleCheckboxChange(index, "isInDSSCustody")}
                        />
                        <Label>Child is in DSS custody</Label>
                      </CheckboxField>

                      {child.isInDSSCustody && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                          <Field>
                            <Label htmlFor={`child-${index}-socialWorkerName`}>Social Worker Name</Label>
                            <Input
                              id={`child-${index}-socialWorkerName`}
                              type="text"
                              placeholder="Social worker's name"
                              value={child.socialWorkerName}
                              onChange={handleChildChange(index, "socialWorkerName")}
                            />
                          </Field>

                          <Field>
                            <Label htmlFor={`child-${index}-socialWorkerPhone`}>Social Worker Phone</Label>
                            <Input
                              id={`child-${index}-socialWorkerPhone`}
                              type="tel"
                              placeholder="(555) 555-5555"
                              value={child.socialWorkerPhone}
                              onChange={handleChildChange(index, "socialWorkerPhone")}
                            />
                          </Field>
                        </div>
                      )}
                    </div>

                    <Divider soft />

                    {/* Victimization Information */}
                    <div>
                      <h4 className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-3">
                        Victimization Information
                      </h4>

                      <Field>
                        <Label htmlFor={`child-${index}-victimizationType`}>Type of Secondary Victimization</Label>
                        <Select
                          id={`child-${index}-victimizationType`}
                          value={child.victimizationType}
                          onChange={handleChildChange(index, "victimizationType")}
                        >
                          <option value="">Select...</option>
                          {VICTIMIZATION_TYPE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Select>
                      </Field>

                      <CheckboxField className="mt-4">
                        <Checkbox
                          checked={child.hasWitnessedAbuse}
                          onChange={handleCheckboxChange(index, "hasWitnessedAbuse")}
                        />
                        <Label>Child has witnessed abuse in the home</Label>
                      </CheckboxField>
                    </div>
                  </div>
                )}
              </div>
            </FieldGroup>
          </Fieldset>
        </div>
      ))}

      {/* Validation message */}
      {!isValid && (
        <p className="text-sm text-amber-600 dark:text-amber-400">
          At least one child with first and last name is required
        </p>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <Button type="button" plain onClick={onBack}>
          Back
        </Button>
        <Button type="button" onClick={onNext} disabled={!isValid}>
          Continue to Review
        </Button>
      </div>
    </div>
  );
};

export default ChildrenStep;
